import { useEffect, useRef, useState } from 'react'
import { SITE } from '@/lib/site'
import type { Announcement } from '@/lib/wp'

const DISMISS_KEY = 'mh-announce-dismissed'
const ROTATE_MS = 5000

type Slide = { key: string; text: string; href: string; external?: boolean; icon: 'dot' | 'youtube' }

const YOUTUBE_SLIDE: Slide = {
  key: 'youtube',
  text: 'Follow us on YouTube',
  href: SITE.youtube,
  external: true,
  icon: 'youtube',
}

/**
 * Slim bar pinned above the nav. It rotates every 5s between the newest
 * article (from WordPress) and the YouTube channel. The article is baked into
 * the static HTML at build time (`initial`) so the bar paints immediately (no
 * late LCP), then refreshed on the client. Both slides share one grid cell, so
 * rotating never shifts layout. Rotation pauses on hover and keyboard focus.
 * Dismissible; dismissal is remembered per session.
 */
export function AnnouncementBar({ initial = null }: { initial?: Announcement | null }) {
  const [data, setData] = useState<Announcement | null>(initial)
  const [dismissed, setDismissed] = useState(false)
  const [index, setIndex] = useState(0)
  const [paused, setPaused] = useState(false)
  const rootRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    try {
      if (sessionStorage.getItem(DISMISS_KEY) === '1') setDismissed(true)
    } catch {
      /* ignore */
    }
    const ctrl = new AbortController()
    fetch(`${SITE.wpApi}/mitohacker/v1/announcement`, { signal: ctrl.signal })
      .then((r) => (r.ok ? r.json() : null))
      .then((json: Announcement | null) => {
        setData(json && json.enabled && json.text ? json : null)
      })
      .catch(() => {})
    return () => ctrl.abort()
  }, [])

  const slides: Slide[] = []
  if (data) slides.push({ key: 'article', text: data.text, href: data.link || '#', icon: 'dot' })
  slides.push(YOUTUBE_SLIDE)
  const active = index % slides.length

  useEffect(() => {
    if (slides.length < 2 || paused || dismissed) return
    const t = window.setTimeout(() => setIndex((i) => (i + 1) % slides.length), ROTATE_MS)
    return () => clearTimeout(t)
  }, [index, slides.length, paused, dismissed])

  if (dismissed) return null

  const accent = data?.accent || 'var(--color-energy)'

  return (
    <div
      ref={rootRef}
      className="relative z-50 border-b border-line/70 bg-surface/95 backdrop-blur"
      style={{ ['--bar-accent' as string]: accent }}
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onFocus={() => setPaused(true)}
      onBlur={(e) => {
        if (!rootRef.current?.contains(e.relatedTarget as Node | null)) setPaused(false)
      }}
    >
      <div className="container-page flex items-center justify-center py-2 text-center text-[13px]">
        <div className="grid min-w-0 px-8">
          {slides.map((s, i) => {
            const on = i === active
            return (
              <div
                key={s.key}
                inert={!on}
                aria-hidden={!on}
                className={`flex min-w-0 items-center justify-center gap-3 transition-[opacity,transform] duration-500 ease-out [grid-area:1/1] motion-reduce:transition-none ${
                  on ? 'translate-y-0 opacity-100' : 'pointer-events-none translate-y-1 opacity-0'
                }`}
              >
                {s.icon === 'dot' ? (
                  <span
                    aria-hidden
                    className="hidden h-1.5 w-1.5 shrink-0 rounded-full sm:block"
                    style={{ background: 'var(--bar-accent)', boxShadow: '0 0 10px var(--bar-accent)' }}
                  />
                ) : (
                  <svg aria-hidden width="18" height="13" viewBox="0 0 18 13" className="shrink-0">
                    <rect width="18" height="13" rx="3.5" fill="#FF0000" />
                    <path d="M7.2 3.6v5.8L12 6.5z" fill="#fff" />
                  </svg>
                )}
                <a
                  href={s.href}
                  {...(s.external ? { target: '_blank', rel: 'noopener' } : {})}
                  className="truncate font-medium text-mist transition-colors hover:text-energy"
                >
                  {s.text}
                </a>
              </div>
            )
          })}
        </div>
        <button
          type="button"
          aria-label="Dismiss announcement"
          onClick={() => {
            try {
              sessionStorage.setItem(DISMISS_KEY, '1')
            } catch {
              /* ignore */
            }
            setDismissed(true)
          }}
          className="absolute right-4 grid h-6 w-6 place-items-center rounded-full text-fog transition-colors hover:bg-line hover:text-mist"
        >
          <svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden>
            <path d="M1 1l10 10M11 1L1 11" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
          </svg>
        </button>
      </div>
    </div>
  )
}
